// @ts-nocheck -- Legacy JavaScript compatibility boundary; runtime behavior intentionally preserved.
import { createTheme } from "@mui/material/styles";

const palette = {
  primary: {
    main: "#EB5B1D",
    gradient: "linear-gradient(136.48deg, #FF823C 0%, #FFA742 97.54%)",
  },
  secondary: {
    main: "#054798",
    gradient: "linear-gradient(135deg, #1C4F97 0%, #068CD7 100%)",
  },
  text: {
    contrast: "#fff",
    light: "#606065",
  },
  background: {
    main: "#f2f2f2",
    smoke: "#d2d2d2",
    alternate: "#f7f7f7",
    white: "#fff",
    gradient: "linear-gradient(44.94deg, #0A3689 -30.17%, #5247B5 113.93%)",
    smokeAlternate: "#D8D8D8",
  },
};

const breakPoint = {
  values: {
    xs: 0,
    sm: 600,
    md: 900,
    lg: 1200,
    xl: 1536,
  },
};

export const theme = createTheme({
  palette,
  breakPoint,
  typography: {
    fontFamily: "Manrope, sans-serif",
    body1: {
      fontSize: "14px",
    },
    subtitle1: {
      color: "#545454",
    },
    subtitle2: {
      fontWeight: 200,
    },
    sectionTitle: {
      fontFamily: "Manrope, sans-serif",
      fontWeight: 600,
      textTransform: "uppercase",
      fontSize: 13,
    },
    caption: {
      color: "#555",
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          margin: 0,
          color: "rgba(0, 0, 0, 0.87)",
          fontFamily: "Manrope,sans-serif",
          fontWeight: 500,
          fontSize: "1rem",
          lineHeight: 1.5,
          backgroundColor: "#f2f2f2",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
        },
        containedPrimary: ({ disabled }) => ({
          background: disabled ? null : palette.primary.gradient,
        }),
        containedSecondary: ({ disabled }) => ({
          background: disabled ? null : palette.secondary.gradient,
        }),
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        groupLabel: {
          color: "#2751b8",
          backgroundColor: "#fafafa",
        },
      },
    },
    MuiFilledInput: {
      defaultProps: {
        hiddenLabel: true,
      },
      styleOverrides: {
        root: {
          underline: null,
          "&&&::before": {
            borderBottom: "none",
          },
          "&::after": {
            borderBottom: "none",
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
        },
      },
    },
  },
  constants: {
    DRAWER_WIDTH: 250,
  },
  divider: {
    background: "#2751b8",
  },
  custom: {
    dialog: {
      blueBackground: "#3178a6",
    },
  },
  codeVerification: {
    helperText: {
      color: "#FF0000",
      fontSize: "14px",
    },
  },
  moneyTransfer: {
    tabs: {
      width: "100%",
      "& .MuiTabs-flexContainer": {
        justifyContent: "space-around",
        border: "1px solid #ddd",
        py: 0.625,
        px: 1.375,
      },
      "& .MuiTab-root": {
        minWidth: "50%",
      },
      "& .Mui-selected": {
        background: "linear-gradient(180deg, rgba(28,79,151,1) 0%, rgba(6,140,215,1) 100%)",
        color: "#FFFF",
        borderRadius: 1,
      },
    },
    warning: {
      box: {
        border: "1px solid #EDEDED",
        mt: 3,
        width: "fit-content",
        p: 2,
      },
    },
  },
  modules: {
    account: {
      try: {
        main: "#F51615",
      },
      eur: {
        main: "#FF8C3E",
      },
      usd: {
        main: "#1C2BD2",
      },
      gbp: {
        main: "#F51615",
      },
    },
  },
  dataGrid: {
    default: {
      pagination: {
        "& .MuiDataGrid-footerContainer": {
          borderTop: "1px solid rgba(224, 224, 224, 1)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
        },
        "& .MuiDataGrid-cell": {
          fontSize: "1em",
        },
        "& .MuiDataGrid-columnHeaderTitle": {
          fontWeight: "bold",
          fontSize: "1em",
        },
        "& .MuiPagination-ul": {
          width: "100%",
          display: "flex",
          justifyContent: "center",
        },
        "& .Mui-selected": {
          backgroundColor: "transparent !important",
          color: "#EB5B1D !important",
          fontWeight: "700",
        },
        "& .MuiPaginationItem-text": {
          backgroundColor: palette.background.smoke,
          fontWeight: "700",
          color: palette.secondary.main,
          borderRadius: 2,
          height: "32px",
        },
        "& .MuiButtonBase-root": {
          ":hover": {
            backgroundColor: "transparent",
          },
        },
      },
      box: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 1,
        mr: 2,
      },
    },
    upt: {
      pagination: {
        "& .MuiDataGrid-footerContainer": {
          borderTop: "1px solid rgba(224, 224, 224, 1)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
        },
        "& .MuiDataGrid-cell": {
          fontSize: "1em",
        },
        "& .MuiDataGrid-columnHeaderTitle": {
          fontWeight: "bold",
          fontSize: "1em",
        },
        "& .MuiPagination-ul": {
          width: "100%",
          display: "flex",
          justifyContent: "center",
        },
        "& button[type='next']": {
          position: "absolute",
          right: 0,
          height: 0,
          fontWeight: "700",
        },
        "& button[type='previous']": {
          position: "absolute",
          left: 0,
          height: 0,
          fontWeight: "700",
        },
        "& .Mui-selected": {
          backgroundColor: "transparent !important",
          color: "#EB5B1D !important",
          fontWeight: "700",
        },
        "& .MuiPaginationItem-text": {
          backgroundColor: palette.background.smoke,
          fontWeight: "700",
          color: palette.secondary.main,
          borderRadius: 2,
          height: "32px",
        },
        "& .MuiButtonBase-root": {
          ":hover": {
            backgroundColor: "transparent",
          },
        },
      },
      box: {
        display: "flex",
        width: "100%",
        justifyContent: "center",
        alignItems: "center",
        pt: 1,
        color: palette.primary.main,
      },
      toolbar: {
        box: {
          maxWidth: 350,
          backgroundColor: "#F3F3F3",
          height: "3rem",
          alignItems: "center",
          display: "flex",
          border: "1px solid #dddd",
          borderRadius: 2,
        },
      },
    },
  },
});
